import React, { useState, useEffect } from 'react';
import api from '../api';
import './Database.css';
import Navbar from '../Page/Navbar';
import Loader from '../Loader/Loader';

const Database = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState('astres');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Configuration des remplacements
    const symbolMap = {
        // Signes
        'Bélier': 'a',
        'Taureau': 'b',
        'Gémeaux': 'c',
        'Cancer': 'd',
        'Lion': 'e',
        'Vierge': 'f',
        'Balance': 'g',
        'Scorpion': 'h',
        'Sagittaire': 'i',
        'Capricorne': 'j',
        'Verseau': 'k',
        'Poissons': 'l',
        // Planètes
        'Soleil': 'A',
        'Lune': 'B',
        'Mercure': 'C',
        'Vénus': 'D',
        'Mars': 'E',
        'Jupiter': 'F',
        'Saturne': 'G',
        'Uranus': 'H',
        'Neptune': 'I',
        'Pluton': 'K'
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await api.get('/get_astrological_database');
                if (response.data.success) {
                    setData(response.data.data);
                } else {
                    setError(response.data.error || 'Une erreur est survenue');
                }
            } catch (err) {
                setError(err.message || 'Une erreur est survenue');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const getItems = () => {
        if (!data || !data[activeCategory]) return [];
        return data[activeCategory];
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setSelectedItem(null);
        setMobileMenuOpen(false);
    };

    const handleItemSelect = (item) => {
        setSelectedItem(item);
        setMobileMenuOpen(false);
    };

    const getCategoryLabel = (category) => {
        switch (category) {
            case 'astres': return 'Planètes';
            case 'signes': return 'Signes';
            case 'maisons': return 'Maisons';
            case 'aspects': return 'Aspects';
            default: return category;
        }
    };

    const renderSymbol = (item) => {
        if (!item.symbole) return null;

        if (activeCategory === 'signes' && symbolMap[item.nom]) {
            return (
                <span className={`astro-symbol ${item.nom.toLowerCase()}-symbol`}>
                    {symbolMap[item.nom]}
                </span>
            );
        } else if (activeCategory === 'astres' && symbolMap[item.nom]) {
            return (
                <span className={`astro-symbol ${item.nom.toLowerCase()}-symbol`}>
                    {symbolMap[item.nom]}
                </span>
            );
        } else {
            return <span className="symbol">{item.symbole}</span>;
        }
    };

    const renderItemsList = () => {
        const items = getItems();

        return (
            <div className="items-list">
                {items.length === 0 ? (
                    <p className="no-results">Aucun résultat trouvé</p>
                ) : (
                    <ul>
                        {items.map((item, index) => (
                            <li
                                key={index}
                                className={selectedItem && (selectedItem.id === item.id) ? 'active' : ''}
                                onClick={() => handleItemSelect(item)}
                            >
                                {activeCategory === 'maisons' ? (
                                    <span className={`maison${item.numero}-text`}>
                                        Maison {item.numero}
                                    </span>
                                ) : (
                                    <>
                                        <span className={activeCategory === 'signes' ? 'sign-text' : activeCategory === 'astres' ? 'planet-text' : ''}>
                                            {item.nom}
                                            {renderSymbol(item)}
                                        </span>
                                        {activeCategory === 'aspects' && <span className="angle">({item.angle}°)</span>}
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const renderItemDetail = () => {
        if (!selectedItem) {
            return (
                <div className="no-item-selected">
                    <p>Sélectionnez un élément pour voir les détails</p>
                </div>
            );
        }

        return (
            <div className="item-detail">
                <div className="item-header">
                    {activeCategory === 'maisons' ? (
                        <h3>
                            <span className={`maison${selectedItem.numero}-text`}>
                                Maison {selectedItem.numero}
                            </span>
                        </h3>
                    ) : (
                        <h3>
                            <span className={activeCategory === 'signes' ? 'sign-text' : activeCategory === 'astres' ? 'planet-text' : ''}>
                                {selectedItem.nom}
                                {renderSymbol(selectedItem)}
                            </span>
                            {activeCategory === 'aspects' && <span className="angle">({selectedItem.angle}°)</span>}
                        </h3>
                    )}
                </div>

                {activeCategory === 'signes' && (
                    <div className="item-properties">
                        <p><strong>Élément:</strong> {selectedItem.element}</p>
                        <p><strong>Mode:</strong> {selectedItem.mode}</p>
                    </div>
                )}

                {activeCategory === 'aspects' && (
                    <div className="item-properties">
                        <p><strong>Type:</strong> {selectedItem.type}</p>
                        <p><strong>Tolérance:</strong> {selectedItem.tolerance}°</p>
                        {selectedItem.description_courte && (
                            <p className="description-courte">{selectedItem.description_courte}</p>
                        )}
                    </div>
                )}

                <div className="item-description">
                    <h4>Description</h4>
                    <p>{selectedItem.description}</p>
                </div>
            </div>
        );
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    if (loading) return (
        <>
            <Navbar />
            <Loader />
        </>);

    if (error) {
        return (
            <>
                <Navbar />
                <div className="container database-container">
                    <div className="error-message panel">
                        <h2>Erreur</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="container database-container">
                <header className="section-header">
                    <h1>Base de Données Astrologique</h1>
                    <p>Explorez notre référentiel complet d'informations astrologiques. Découvrez les significations des planètes, signes, maisons et aspects qui constituent le fondement de l'analyse astrologique.</p>
                </header>

                <div className="database-content">
                    <div className="categories-tabs">
                        {['astres', 'signes', 'maisons', 'aspects'].map(category => (
                            <button
                                key={category}
                                className={activeCategory === category ? 'active' : ''}
                                onClick={() => handleCategoryChange(category)}
                            >
                                {getCategoryLabel(category)}
                            </button>
                        ))}
                    </div>


                    <div className={`database-main ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                        <div className="items-panel panel">
                            <h2 className="section-title">{getCategoryLabel(activeCategory)}</h2>
                            {renderItemsList()}
                        </div>

                        <div className="detail-panel panel">
                            <h2 className="section-title">Détails</h2>
                            {renderItemDetail()}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Database; 